import { keywordsMeta } from "@/lib/seo/keywords";
import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Section } from "@/components/site/Section";
const FaqSearch = lazy(() => import("@/components/site/faq/FaqSearch").then(m => ({ default: m.FaqSearch })));
import { faqCategories, topFaqs } from "@/content/faq-data";
import { Phone, Mail, Calendar, ArrowRight } from "lucide-react";
import { EditorialHero } from "@/components/site/wow/EditorialHero";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { aeoGeo } from "@/lib/seo/schema";

const SITE_ORIGIN = "https://fenny.se";

const allFaqs = faqCategories.flatMap((c) => c.faqs);

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: allFaqs.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

const breadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Hem", item: `${SITE_ORIGIN}/` },
    { "@type": "ListItem", position: 2, name: "FAQ", item: `${SITE_ORIGIN}/faq` },
  ],
};

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      ...keywordsMeta("/faq"),
      { title: "FAQ — Vanliga frågor om film | Fenny" },
      { name: "description", content: "Vanliga frågor om filmproduktion hos Fenny. Sök svar om Pulse, Prime, priser, rättigheter och leverans." },
      { property: "og:title", content: "FAQ — film | Fenny" },
      { property: "og:description", content: "Sökbar FAQ om Pulse, Prime, Pilot, priser, rättigheter och leveranser." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://fenny.se/faq" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/faq" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/faq", name: "FAQ — Vanliga frågor om Fenny", description: "Sök bland alla våra svar om film — abonnemang, process, pris, rättigheter och leverans."})) },
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbJsonLd) },
    ],
  }),
  component: FaqPage,
});

function FaqPage() {
  return (
    <div>
      <EditorialHero
        eyebrow="FAQ"
        title="Svar på allt ni brukar undra."
        highlight={[1, 2]}
        tone="butter"
        align="center"
        lede={`Sök, filtrera, hitta. ${allFaqs.length} frågor och svar — uppdaterat löpande utifrån verkliga samtal.`}
      />

      <Section className="pt-4">
        <Suspense fallback={<div style={{ minHeight: 600 }} />}>
          <FaqSearch categories={faqCategories} top={topFaqs} />
        </Suspense>


        <div className="mt-12 text-center">
          <Link
            to="/fragor"
            className="group inline-flex items-center gap-2 text-sm font-medium text-foreground/80 transition-colors hover:text-foreground"
          >
            Se våra utförliga frågor &amp; svar om film och pris
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>

        </div>


        <div className="mt-20 rounded-3xl border border-white/10 bg-surface/40 p-8 md:p-12">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-balance text-2xl font-semibold md:text-3xl">Hittade ni inte svaret?</h2>
            <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
              Välj kanalen som passar er bäst — vi svarar samma dag på vardagar.
            </p>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            <a href="tel:+46848004706" className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-background/40 p-5 transition-colors hover:border-mint/40">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-mint/10 text-mint"><Phone className="h-5 w-5" /></span>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Ring</div>
                <div className="text-sm font-semibold">08-480 04 706</div>
              </div>
            </a>
            <a href="mailto:hey@fenny.se" className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-background/40 p-5 transition-colors hover:border-mint/40">
              <span className="grid h-11 w-11 place-items-center rounded-full bg-mint/10 text-mint"><Mail className="h-5 w-5" /></span>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Mejla</div>
                <div className="text-sm font-semibold">hey@fenny.se</div>
              </div>
            </a>
            <a
              href="/kontakt"
              onClick={() => { import("@/lib/analytics").then((a) => a.trackCta("book_strategy_meeting", { location: "faq_bottom" })); }}
              className="group flex items-center gap-4 rounded-2xl border border-mint/30 bg-mint/10 p-5 transition-colors hover:border-mint/60"
            >
              <span className="grid h-11 w-11 place-items-center rounded-full bg-mint text-mint-foreground"><Calendar className="h-5 w-5" /></span>
              <div>
                <div className="text-xs uppercase tracking-widest text-mint/90">Boka</div>
                <div className="text-sm font-semibold">Strategimöte (30 min)</div>
              </div>
            </a>
          </div>
        </div>
      </Section>

      <SignatureCta
        eyebrow="Fortfarande osäker?"
        title="Boka 30 minuter. Vi svarar live."
        body="Vi går igenom era frågor på riktigt, säger ärligt om vi passar och designar er Pulse om vi gör det."
        tone="butter"
      />
    </div>
  );
}
